import React, { Component } from 'react';
import moment from 'moment';
import withStyles from '@mui/styles/withStyles';
import gql from 'graphql-tag';
import { FormControlLabel, Checkbox } from '@mui/material';
import Typography from '../component/Typography';
import TextField from '../component/form/TextField';
import PrimaryButton from '../component/form/PrimaryButton';
import GraphqlGrid from '../component/form/v2/GraphqlGrid';
import LinkButton from '../component/form/LinkButton';
import BrandDot from '../component/table/BrandDot';
import { getOfficeFromKey } from '../util/brands';
import { joinDefined, prettyDisposal } from '../util/strings';
import { withErrorBoundary } from '@sentry/react';
import ErrorFallback from '../component/ErrorFallback';

const searchByPhoneQuery = gql`
    query PhoneSearch($number: String, $assignedOnly: Boolean, $limit: Int, $offset: Int) {
        searchByPhone(number: $number, assignedOnly: $assignedOnly, limit: $limit, offset: $offset) {
            edges {
                node {
                    id
                    recordType
                    assignedToMe
                    role
                    contactName
                    comment
                    funeral {
                        ID
                        LegacyKey
                        FirstName
                        Surname
                        Created
                        Disposal {
                            CrematedOrBuried
                            Date
                        }
                        PlaceOfService {
                            Type
                        }
                    }
                    enquiry {
                        ID
                        LegacyKey
                        GivenName
                        Surname
                        EnquiryType
                        Created
                    }
                    prearrangement {
                        ID
                        LegacyKey
                        FirstName
                        Surname
                        Created
                    }
                }
            }
            pageInfo {
                totalCount
                assignedCount
                allCount
            }
        }
    }
`;

const recordOf = node => node.funeral || node.enquiry || node.prearrangement || {};

const linkFor = node => {
    const r = recordOf(node);
    if (node.funeral) return `/funeral/${r.LegacyKey}/${r.ID}`;
    if (node.prearrangement) return `/prearrangement/${r.LegacyKey}/${r.ID}`;
    if (node.enquiry) return `/quotes/enquiry/${r.LegacyKey}/${r.ID}`;
    return '#';
};

class PhoneSearch extends Component {
    state = {
        number: '',
        submitted: '',
        assignedOnly: true,
        autoFlipped: false,
        autoFlipKey: null,
        fastForward: false,
        fastForwardedKey: null
    };

    componentDidMount() {
        const params = new URLSearchParams((this.props.location && this.props.location.search) || '');
        const number = (params.get('number') || '').replace(/[^0-9]/g, '');
        const assignedParam = params.get('assignedToMe');
        const next = {
            assignedOnly: assignedParam === null ? true : assignedParam !== 'false',
            fastForward: params.get('fastForward') === 'true'
        };
        if (number) {
            next.number = number;
            next.submitted = number;
        }
        this.setState(next);
    }

    onSubmit = e => {
        e.preventDefault();
        this.setState({
            submitted: this.state.number.trim(),
            assignedOnly: true,
            autoFlipped: false,
            fastForward: false
        });
    };

    onToggle = () => {
        this.setState({ assignedOnly: !this.state.assignedOnly, autoFlipped: false });
    };

    onData = (pageInfo, rows) => {
        if (
            this.state.assignedOnly &&
            pageInfo &&
            pageInfo.assignedCount === 0 &&
            pageInfo.allCount > 0 &&
            this.state.autoFlipKey !== this.state.submitted
        ) {
            this.setState({ assignedOnly: false, autoFlipped: true, autoFlipKey: this.state.submitted });
            return;
        }

        if (
            this.state.fastForward &&
            this.state.fastForwardedKey !== this.state.submitted &&
            pageInfo &&
            pageInfo.totalCount === 1 &&
            rows &&
            rows.length === 1 &&
            rows[0].node
        ) {
            this.setState({ fastForwardedKey: this.state.submitted });
            const path = linkFor(rows[0].node);
            if (path !== '#') this.props.history.push(path);
        }
    };

    onRowClick = (e, row) => {
        e.preventDefault();
        const path = linkFor(row.node);
        if (path !== '#') this.props.history.push(path);
    };

    columns = [
        {
            name: 'Created',
            queryField: 'node.recordType',
            width: 150,
            render: (val, { node }) => {
                const created = recordOf(node).Created;
                return created ? moment(created).format('D/MM/YYYY h:mma') : '';
            }
        },
        {
            name: 'Key',
            queryField: 'node.recordType',
            width: 120,
            noWrap: true,
            render: (val, { node }) => {
                const r = recordOf(node);
                const { color, label } = getOfficeFromKey(r.LegacyKey) || { color: 'grey', label: '' };
                return (
                    <LinkButton href={linkFor(node)}>
                        <BrandDot dotColour={color} label={r.LegacyKey} title={label} />
                    </LinkButton>
                );
            }
        },
        {
            name: 'Type',
            queryField: 'node.recordType',
            width: 130,
            render: (val, { node }) => node.recordType
        },
        {
            name: 'Deceased',
            queryField: 'node.recordType',
            render: (val, { node }) => {
                const r = recordOf(node);
                return joinDefined([(r.Surname || '').toUpperCase(), r.FirstName || r.GivenName], ' ');
            }
        },
        {
            name: 'Contact',
            queryField: 'node.contactName',
            render: (val, { node }) => joinDefined([node.contactName, node.comment ? `(${node.comment})` : ''], ' ')
        },
        {
            name: 'Role',
            queryField: 'node.role',
            width: 150,
            render: (val, { node }) => node.role
        },
        {
            name: 'Funeral Type',
            queryField: 'node.recordType',
            width: 130,
            render: (val, { node }) => {
                const disposal = node.funeral && node.funeral.Disposal && node.funeral.Disposal.CrematedOrBuried;
                return disposal ? prettyDisposal(disposal, false) : '';
            }
        },
        {
            name: 'Date of Disposal',
            queryField: 'node.recordType',
            width: 160,
            render: (val, { node }) => {
                const date = node.funeral && node.funeral.Disposal && node.funeral.Disposal.Date;
                return date ? moment(date).format('D/MM/YYYY') : '';
            }
        },
        {
            name: 'Service Type',
            queryField: 'node.recordType',
            width: 130,
            render: (val, { node }) =>
                (node.funeral && node.funeral.PlaceOfService && node.funeral.PlaceOfService.Type) || ''
        },
        {
            name: 'Assigned',
            queryField: 'node.assignedToMe',
            width: 110,
            textAlign: 'center',
            render: (val, { node }) => (node.assignedToMe ? 'Yes' : '')
        }
    ];

    render() {
        const { classes } = this.props;
        const { number, submitted, assignedOnly, autoFlipped } = this.state;
        return (
            <div className={classes.root}>
                <Typography variant="headline" gutterBottom className={classes.pageTitle}>
                    Phone Search
                </Typography>
                <form onSubmit={this.onSubmit} className={classes.form}>
                    <TextField
                        fullWidth
                        type="search"
                        placeholder="Enter a phone number..."
                        value={number}
                        onChange={e => this.setState({ number: e.target.value })}
                    />
                    <PrimaryButton type="submit" style={{ marginTop: 0 }}>
                        Search
                    </PrimaryButton>
                </form>
                <FormControlLabel
                    control={<Checkbox checked={assignedOnly} color="primary" onChange={this.onToggle} />}
                    label="Only records assigned to me"
                />
                {autoFlipped && !assignedOnly && (
                    <Typography className={classes.note}>
                        None of the matches are assigned to you — showing all.
                    </Typography>
                )}
                {submitted ? (
                    <GraphqlGrid
                        hideSearch
                        columns={this.columns}
                        noResultsMessage={<Typography>No records found for that number.</Typography>}
                        gqlReadQuery={searchByPhoneQuery}
                        gqlQueryVariables={{ number: submitted, assignedOnly }}
                        gqlReadDataSelector={'searchByPhone.edges'}
                        gqlReadPageInfoSelector={'searchByPhone.pageInfo'}
                        gqlPageSizes={[10, 25, 50]}
                        onData={this.onData}
                        onRowClick={this.onRowClick}
                    />
                ) : (
                    <Typography className={classes.message}>Enter a phone number to search.</Typography>
                )}
            </div>
        );
    }
}

const styles = ({ typography, palette, funeralHome, spacing }) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: spacing(2)
    },
    pageTitle: {
        fontWeight: typography.fontWeightRegular,
        color: palette.contentForeground[funeralHome]
    },
    form: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing(1),
        maxWidth: 520
    },
    note: {
        color: palette.text.secondary,
        marginBottom: spacing(1),
        fontStyle: 'italic'
    },
    message: {
        marginTop: spacing(3),
        color: palette.text.secondary
    }
});

export default withErrorBoundary(withStyles(styles)(PhoneSearch), { fallback: ErrorFallback });
